/**
 * Timing tokens — OPTIONAL starter values, in milliseconds.
 *
 * Like `easing.ts`, these are conveniences, not a mandated house style; a game
 * may override or ignore them. They're tuned for an older-adult audience:
 * slightly slower than mobile-game defaults so animations are readable from
 * across a common room and at low visual acuity. Whatever values a game picks,
 * keep that calibration in mind — don't go below ~150ms for anything the user
 * is meant to follow visually; sub-100ms reads as jitter rather than motion.
 *
 * `prefersReducedMotion()` / `maybe()` below ARE shared mechanical plumbing —
 * every game (DOM or canvas) should honor them. Use them to short-circuit any
 * animation when the user has `prefers-reduced-motion: reduce` or
 * `accessibility.reduceMotion`.
 */

export const timings = {
  /** Press/release affordance. Just enough to feel mechanical. */
  press: 120,
  /** Standard tween for color/opacity changes. */
  fast: 200,
  /** The default. Card entrances, prompt shifts, "the next thing happens." */
  base: 360,
  /** Slow, intentional. Use when something deserves contemplation. */
  slow: 600,
  /** Celebration: confetti, score count-up, the "ta-da" beat. */
  celebrate: 900,
  /**
   * The pause between rounds / questions / words — long enough for the
   * affirmation to land, short enough to keep momentum.
   */
  beat: 1400,
} as const;

export type TimingName = keyof typeof timings;

/**
 * Read the user's reduced-motion preference. Returns true when motion should
 * be minimized (OS preference, in-app calm mode, etc.). Callers may collapse
 * any of the `timings` to ~0 or skip animation entirely when this is true.
 *
 * The hook-free read here is intentional — components that need reactive
 * behavior should subscribe to the matchMedia query themselves; this is the
 * "is it true right now" check for one-shot decisions.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Multiply a timing by 0 when reduced-motion is active. */
export function maybe(ms: number, reduced = prefersReducedMotion()): number {
  return reduced ? 0 : ms;
}
